import React, { useEffect, useState } from "react";
import { api, authHeaders, fileUrl } from "@/api";
import { useI18n } from "@/i18n";
import { CATEGORIES } from "@/constants";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { LogOut, Trash2, Check, Image as ImgIcon, Plus, Star } from "lucide-react";

const TOKEN_KEY = "ap3d_admin_token";

export default function AdminPage() {
  const { t } = useI18n();
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });

  const login = (tk) => {
    setToken(tk);
    try { localStorage.setItem(TOKEN_KEY, tk); } catch {}
  };
  const logout = () => {
    setToken(null);
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono-accent text-xs uppercase tracking-[0.25em] text-brand-blue-light">
              / ADMIN
            </div>
            <h1 className="font-heading mt-2 text-4xl font-black tracking-tighter">{t.admin.title}</h1>
          </div>
          {token && (
            <Button
              onClick={logout}
              data-testid="admin-logout-btn"
              variant="outline"
              className="rounded-none border-white/10 bg-transparent text-white hover:bg-white/5 font-mono-accent uppercase text-xs"
            >
              <LogOut size={14} className="mr-2" />
              {t.admin.logout}
            </Button>
          )}
        </div>

        <div className="mt-10">
          {!token ? <LoginForm onLogin={login} /> : <AdminTabs token={token} />}
        </div>
      </div>
    </div>
  );
}

function LoginForm({ onLogin }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      onLogin(data.token);
    } catch {
      toast.error(t.admin.bad_login);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="admin-login-form"
      className="max-w-md border border-white/10 bg-zinc-900/40 p-8"
    >
      <div>
        <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
          {t.admin.email}
        </Label>
        <Input
          required
          type="email"
          data-testid="admin-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 bg-black/60 border-white/10 focus-visible:ring-brand-blue"
        />
      </div>
      <div className="mt-4">
        <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">
          {t.admin.password}
        </Label>
        <Input
          required
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 bg-black/60 border-white/10 focus-visible:ring-brand-blue"
        />
      </div>
      <Button
        type="submit"
        disabled={submitting}
        data-testid="admin-login-submit"
        className="mt-6 w-full bg-brand-blue hover:bg-brand-blue-light text-white rounded-none font-mono-accent uppercase text-xs tracking-widest py-5"
      >
        {submitting ? t.admin.logging : t.admin.login}
      </Button>
    </form>
  );
}

function AdminTabs({ token }) {
  const { t } = useI18n();
  return (
    <Tabs defaultValue="orders" className="w-full">
      <TabsList className="bg-transparent border border-white/10 rounded-none p-0 h-auto">
        {["orders", "reviews", "projects"].map((k) => (
          <TabsTrigger
            key={k}
            value={k}
            data-testid={`admin-tab-${k}`}
            className="rounded-none px-6 py-3 font-mono-accent uppercase text-xs tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white data-[state=active]:shadow-none"
          >
            {t.admin.tabs[k]}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="orders" className="mt-6"><OrdersPanel token={token} /></TabsContent>
      <TabsContent value="reviews" className="mt-6"><ReviewsPanel token={token} /></TabsContent>
      <TabsContent value="projects" className="mt-6"><ProjectsPanel token={token} /></TabsContent>
    </Tabs>
  );
}

function OrdersPanel({ token }) {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders", { headers: authHeaders(token) }).then(r => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status }, { headers: authHeaders(token) });
    load();
  };
  const del = async (id) => {
    await api.delete(`/admin/orders/${id}`, { headers: authHeaders(token) });
    load();
  };

  if (orders.length === 0) {
    return <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500 font-mono-accent text-xs uppercase">No orders yet</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-orders-list">
      {orders.map((o) => (
        <div
          key={o.id}
          data-testid={`admin-order-${o.id}`}
          className="border border-white/10 bg-zinc-900/40 p-6 grid md:grid-cols-[1fr_auto] gap-6"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-accent text-[10px] uppercase tracking-widest bg-brand-blue/15 border border-brand-blue/40 text-brand-blue-light px-2 py-0.5">
                {o.category.replace(/_/g, " ")}
              </span>
              <StatusBadge status={o.status} />
              <span className="text-[11px] font-mono-accent text-zinc-500">
                {new Date(o.created_at).toLocaleString()}
              </span>
            </div>
            <div className="font-heading font-bold text-lg">{o.name}</div>
            <div className="text-sm text-zinc-400">
              {o.email} {o.phone ? ` · ${o.phone}` : ""}
            </div>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{o.description}</p>
            {o.file_id && (
              <a
                href={fileUrl(o.file_id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-2 text-xs font-mono-accent uppercase text-brand-blue-light hover:underline"
                data-testid={`admin-order-file-${o.id}`}
              >
                <ImgIcon size={14} /> {t.admin.order.view_image}
              </a>
            )}
          </div>
          <div className="flex md:flex-col gap-2 md:min-w-[200px]">
            <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
              <SelectTrigger
                data-testid={`admin-order-status-${o.id}`}
                className="rounded-none bg-black/60 border-white/10 font-mono-accent text-xs uppercase"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0d0d0d] border-white/10">
                <SelectItem value="new">{t.admin.order.new}</SelectItem>
                <SelectItem value="in_progress">{t.admin.order.in_progress}</SelectItem>
                <SelectItem value="completed">{t.admin.order.completed}</SelectItem>
                <SelectItem value="cancelled">{t.admin.order.cancelled}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => del(o.id)}
              data-testid={`admin-order-delete-${o.id}`}
              className="rounded-none bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 font-mono-accent text-xs uppercase"
            >
              <Trash2 size={14} className="mr-1" /> {t.admin.order.delete}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const { t } = useI18n();
  const styles = {
    new: "bg-blue-500/15 border-blue-500/40 text-blue-300",
    in_progress: "bg-amber-500/15 border-amber-500/40 text-amber-300",
    completed: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
    cancelled: "bg-zinc-500/15 border-zinc-500/40 text-zinc-400",
  };
  return (
    <span className={`font-mono-accent text-[10px] uppercase tracking-widest border px-2 py-0.5 ${styles[status] || styles.new}`}>
      {t.admin.order[status] || status}
    </span>
  );
}

function ReviewsPanel({ token }) {
  const { t } = useI18n();
  const [reviews, setReviews] = useState([]);
  const load = () => api.get("/admin/reviews", { headers: authHeaders(token) }).then(r => setReviews(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await api.patch(`/admin/reviews/${id}/approve`, {}, { headers: authHeaders(token) });
    load();
  };
  const del = async (id) => {
    await api.delete(`/admin/reviews/${id}`, { headers: authHeaders(token) });
    load();
  };

  if (reviews.length === 0) {
    return <div className="border border-dashed border-white/10 p-12 text-center text-zinc-500 font-mono-accent text-xs uppercase">No reviews yet</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-reviews-list">
      {reviews.map((r) => (
        <div
          key={r.id}
          data-testid={`admin-review-${r.id}`}
          className="border border-white/10 bg-zinc-900/40 p-6 grid md:grid-cols-[1fr_auto] gap-4"
        >
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={`font-mono-accent text-[10px] uppercase tracking-widest border px-2 py-0.5 ${
                  r.approved
                    ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                    : "bg-amber-500/15 border-amber-500/40 text-amber-300"
                }`}
              >
                {r.approved ? t.admin.review.approved : t.admin.review.pending}
              </span>
              <span className="font-heading font-bold">{r.name}</span>
              <span className="text-[11px] font-mono-accent text-zinc-500">
                {"★".repeat(r.rating)}
              </span>
            </div>
            <p className="text-sm text-zinc-300 mt-2">{r.comment}</p>
          </div>
          <div className="flex md:flex-col gap-2 md:min-w-[160px]">
            {!r.approved && (
              <Button
                onClick={() => approve(r.id)}
                data-testid={`admin-review-approve-${r.id}`}
                className="bg-brand-blue hover:bg-brand-blue-light rounded-none font-mono-accent text-xs uppercase"
              >
                <Check size={14} className="mr-1" /> {t.admin.review.approve}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => del(r.id)}
              data-testid={`admin-review-delete-${r.id}`}
              className="rounded-none bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 font-mono-accent text-xs uppercase"
            >
              <Trash2 size={14} className="mr-1" /> {t.admin.review.delete}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProjectsPanel({ token }) {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ title: "", category: "people", description: "", image_url: "", featured: false });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/projects").then(r => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/projects", form, { headers: authHeaders(token) });
      toast.success("Project created");
      setForm({ title: "", category: "people", description: "", image_url: "", featured: false });
      load();
    } catch {
      toast.error("Failed to create");
    } finally {
      setCreating(false);
    }
  };
  const del = async (id) => {
    await api.delete(`/admin/projects/${id}`, { headers: authHeaders(token) });
    load();
  };
  const toggleFeatured = async (id, featured) => {
    await api.patch(`/admin/projects/${id}/featured`, { featured }, { headers: authHeaders(token) });
    load();
  };

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-8">
      <form
        onSubmit={create}
        data-testid="admin-project-form"
        className="border border-white/10 bg-zinc-900/40 p-6 space-y-4 h-fit"
      >
        <div className="font-heading font-bold flex items-center gap-2"><Plus size={16} /> {t.admin.project.add}</div>
        <div>
          <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">{t.admin.project.title}</Label>
          <Input required data-testid="admin-project-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-black/60 border-white/10" />
        </div>
        <div>
          <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">{t.admin.project.category}</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="admin-project-category" className="rounded-none bg-black/60 border-white/10 mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0d0d0d] border-white/10">
              {CATEGORIES.map((c) => (
                <SelectItem key={c.key} value={c.key}>{t.categories[c.key].name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">{t.admin.project.desc}</Label>
          <Textarea required rows={3} data-testid="admin-project-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-black/60 border-white/10" />
        </div>
        <div>
          <Label className="text-xs font-mono-accent uppercase tracking-wider text-zinc-400">{t.admin.project.image}</Label>
          <Input required data-testid="admin-project-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1 bg-black/60 border-white/10" placeholder="https://..." />
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="admin-project-featured-checkbox"
            checked={!!form.featured}
            onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            className="accent-amber-400 w-4 h-4"
          />
          <span className="font-mono-accent text-xs uppercase tracking-wider text-amber-300 inline-flex items-center gap-1">
            <Star size={12} fill="currentColor" /> {t.featured}
          </span>
        </label>
        <Button
          type="submit"
          disabled={creating}
          data-testid="admin-project-submit"
          className="w-full bg-brand-blue hover:bg-brand-blue-light rounded-none font-mono-accent uppercase text-xs"
        >
          {creating ? "..." : t.admin.project.create}
        </Button>
      </form>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-projects-list">
        {projects.map((p) => (
          <div key={p.id} data-testid={`admin-project-${p.id}`} className={`border ${p.featured ? "border-amber-300/60" : "border-white/10"} bg-zinc-900/40 overflow-hidden`}>
            <div className="relative">
              <img src={p.image_url} alt={p.title} className="h-40 w-full object-cover" />
              {p.featured && (
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 font-mono-accent text-[10px] font-bold uppercase tracking-[0.2em] text-black bg-amber-300 border border-amber-200 px-2 py-1">
                  <Star size={10} fill="currentColor" />
                  {t.featured}
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="font-mono-accent text-[10px] uppercase tracking-widest text-brand-blue-light">{p.category.replace(/_/g, " ")}</div>
              <div className="font-heading font-bold mt-1">{p.title}</div>
              <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{p.description}</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => toggleFeatured(p.id, !p.featured)}
                  data-testid={`admin-project-featured-${p.id}`}
                  className={`rounded-none font-mono-accent text-xs uppercase ${
                    p.featured
                      ? "bg-amber-400/15 border-amber-300/60 text-amber-200 hover:bg-amber-400/25"
                      : "bg-transparent border-white/15 text-zinc-300 hover:bg-white/5"
                  }`}
                >
                  <Star size={14} className="mr-1" fill={p.featured ? "currentColor" : "none"} />
                  {t.featured}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => del(p.id)}
                  data-testid={`admin-project-delete-${p.id}`}
                  className="rounded-none bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 font-mono-accent text-xs uppercase"
                >
                  <Trash2 size={14} className="mr-1" /> {t.admin.project.delete}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
